// Cross-context clipboard write.
//
// The modern `navigator.clipboard.writeText` API requires the document to be
// a top-level or same-origin-embedded context AND for the browser to grant
// the `clipboard-write` Permissions-Policy. The Emergent editor renders the
// app inside a third-party iframe where that policy is not delegated, so
// calling the API throws NotAllowedError.
//
// Fallback strategy: a hidden textarea + `document.execCommand('copy')`.
// It's deprecated but universally supported and works in restricted iframe
// contexts. We stay silent if BOTH paths fail so callers can decide whether
// to surface the error to the user.
export async function copyToClipboard(text) {
  const value = String(text ?? "");
  // 1) Try the modern API. If Permissions-Policy blocks it we'll fall
  //    through to execCommand. If it succeeds, we're done.
  if (typeof navigator !== "undefined" && navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(value);
      return true;
    } catch {
      // Fall through to legacy path — do not rethrow.
    }
  }

  // 2) Legacy fallback using a hidden textarea. Works inside sandboxed
  //    iframes as long as the copy is triggered from a user gesture.
  try {
    const ta = document.createElement("textarea");
    ta.value = value;
    // Keep it visually inert but focusable and selectable.
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.top = "0";
    ta.style.left = "0";
    ta.style.width = "1px";
    ta.style.height = "1px";
    ta.style.padding = "0";
    ta.style.border = "0";
    ta.style.outline = "none";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    ta.setSelectionRange(0, value.length);
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return !!ok;
  } catch {
    return false;
  }
}
